import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    { label: 'Create', icon: 'FileText', color: '#F58A54' },
    { label: 'Connect', icon: 'Users', color: '#F06F6B' },
    { label: 'Compare', icon: 'BarChart3', color: '#E0528A' },
    { label: 'Buy', icon: 'CheckCircle', color: '#F58A54' },
  ];

  const decorativeIcons = [
    // Left column - 7 icons evenly spaced top to bottom
    { name: 'Home',        label: 'Home',        top: '14%', left: '1%',  size: 32, rotate: '-12deg', opacity: 0.5 },
    { name: 'Car',         label: 'Auto',        top: '26%', left: '3%',  size: 28, rotate: '8deg',  opacity: 0.45 },
    { name: 'ShieldCheck', label: 'Insurance',   top: '38%', left: '1%',  size: 28, rotate: '-8deg', opacity: 0.46 },
    { name: 'Heart',       label: 'Wedding',     top: '50%', left: '3%',  size: 28, rotate: '14deg', opacity: 0.45 },
    { name: 'Sofa',        label: 'Furniture',   top: '62%', left: '1%',  size: 29, rotate: '6deg',  opacity: 0.45 },
    { name: 'Baby',        label: 'Childcare',   top: '74%', left: '3%',  size: 28, rotate: '-7deg', opacity: 0.46 },
    { name: 'Music',       label: 'Concert',     top: '86%', left: '1%',  size: 28, rotate: '10deg', opacity: 0.45 },
    // Right column - 7 icons evenly spaced top to bottom
    { name: 'Laptop',      label: 'Electronics', top: '14%', right: '1%', size: 32, rotate: '10deg',  opacity: 0.45 },
    { name: 'CreditCard',  label: 'Loan',        top: '26%', right: '3%', size: 30, rotate: '-6deg',  opacity: 0.48 },
    { name: 'Sailboat',    label: 'Boat',        top: '38%', right: '1%', size: 29, rotate: '9deg',   opacity: 0.45 },
    { name: 'Hammer',      label: 'Services',    top: '50%', right: '3%', size: 30, rotate: '-10deg', opacity: 0.48 },
    { name: 'CreditCard',  label: 'Credit Card', top: '62%', right: '1%', size: 30, rotate: '11deg',  opacity: 0.47 },
    { name: 'Plane',       label: 'Travel',      top: '74%', right: '3%', size: 30, rotate: '-13deg', opacity: 0.46 },
    { name: 'Truck',       label: 'Moving',      top: '86%', right: '1%', size: 30, rotate: '-8deg',  opacity: 0.47 },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps?.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative overflow-hidden pt-36 pb-20" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute bottom-0 -left-16 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/3 blur-3xl" />

        {/* Decorative category icons */}
        {decorativeIcons?.map((icon) => (
          <div
            key={icon?.name}
            className="absolute flex flex-col items-center gap-1"
            style={{
              top: icon?.top,
              left: icon?.left,
              right: icon?.right,
              bottom: icon?.bottom,
              opacity: icon?.opacity,
              transform: `rotate(${icon?.rotate})`,
            }}
          >
            <div className="w-14 h-14 rounded-2xl bg-white/25 border border-white/35 flex items-center justify-center backdrop-blur-sm">
              <Icon name={icon?.name} size={icon?.size} color="white" />
            </div>
            <span className="text-white text-xs font-medium tracking-wide">{icon?.label}</span>
          </div>
        ))}
      </div>
      <div className="relative max-w-6xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-14">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5">
              <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-white/90 text-sm font-medium tracking-wide">Big Purchases made easy.</span>
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight">
            How Stackd Works
          </h1>
          <p className="text-white/75 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
Big purchases shouldn't feel overwhelming. Stackd gives you the clarity, transparency, and speed you need to choose what's right for you. Buy Better. Buy Smarter. Always.
          </p>
        </div>

        {/* Animated step flow */}
        <div className="flex items-center justify-center gap-0 md:gap-2">
          {steps?.map((step, index) => (
            <React.Fragment key={step?.label}>
              <div
                className={`flex flex-col items-center gap-2 transition-all duration-500 ${
                  activeStep === index ? 'scale-110' : 'scale-95 opacity-60'
                }`}
              >
                <div
                  className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                    activeStep === index
                      ? 'bg-white shadow-lg shadow-black/20'
                      : 'bg-white/20'
                  }`}
                >
                  <Icon
                    name={step?.icon}
                    size={24}
                    color={activeStep === index ? '#F06F6B' : 'white'}
                  />
                </div>
                <span
                  className={`text-xs md:text-sm font-semibold transition-all duration-500 ${
                    activeStep === index ? 'text-white' : 'text-white/60'
                  }`}
                >
                  {step?.label}
                </span>
              </div>
              {index < steps?.length - 1 && (
                <div className="flex items-center mx-1 md:mx-3 mb-5">
                  <div
                    className={`h-0.5 w-8 md:w-16 transition-all duration-500 ${
                      activeStep > index ? 'bg-white' : 'bg-white/25'
                    }`}
                  />
                  <Icon
                    name="ChevronRight"
                    size={14}
                    color={activeStep > index ? 'white' : 'rgba(255,255,255,0.3)'}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <button
            onClick={() => navigate('/create-project-flow')}
            className="px-8 py-3.5 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            Start Your Stack
          </button>
          <button
            onClick={() => navigate('/all-projects')}
            className="px-8 py-3.5 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-200"
          >
            View Stacks
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
